import { useState } from "react";
import { BiCalendarEvent, BiCheck, BiDollarCircle, BiPackage } from "react-icons/bi";
import eventScreen from "../../assets/homepage/EventScreen.png";
import financeScreen from "../../assets/homepage/FinanceScreen-web.png";
import inventoryScreen from "../../assets/homepage/InventoryScreen-web.png";

const tabs = [
  {
    key: "events",
    label: "Events",
    icon: BiCalendarEvent,
    image: eventScreen,
    alt: "VendorOrbis Events screen listing upcoming fairs with dates and weather",
    title: "Every show on one calendar",
    description:
      "Add a market in under a minute with its dates, hours, location, and booth fee. The forecast sits right on the event so you know what to expect.",
    points: [
      "Daily schedules for multi-day festivals",
      "Weather at the venue, plus a packing list",
      "Assign staff to the shows they’re working",
    ],
  },
  {
    key: "inventory",
    label: "Inventory",
    icon: BiPackage,
    image: inventoryScreen,
    alt: "VendorOrbis Inventory screen with Main, Show A, and Show B inventory pools",
    title: "Know exactly what you brought",
    description:
      "Allocate items to each event, then watch counts drop as you sell. Filters show what’s low, out, or flying off the table.",
    points: [
      "Low-stock alerts and best-seller tags",
      "Separate inventory pools for each show",
      "Bulk import from Excel or a Square export",
    ],
  },
  {
    key: "finance",
    label: "Finance",
    icon: BiDollarCircle,
    image: financeScreen,
    alt: "VendorOrbis Finance Overview with net profit, revenue, costs, and payouts",
    title: "Profit, not just sales",
    description:
      "Booth fees, cost of goods, refunds, discounts, and staff pay all come out of the total, so the number you see is what you actually made.",
    points: [
      "Profit and margin per event or any date range",
      "Business expenses by category",
      "Monthly sales tax reports with CSV export",
    ],
  },
];

const ProductTour = () => {
  const [activeKey, setActiveKey] = useState(tabs[0].key);
  const active = tabs.find((tab) => tab.key === activeKey) ?? tabs[0];

  return (
    <section className="bg-[#f6f7fb] px-5 py-24 md:px-8 md:py-32">
      <div className="mx-auto max-w-6xl">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-blue-600">
            The app
          </p>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight text-slate-900 md:text-5xl">
            One app for the whole business
          </h2>
        </div>

        <div
          role="tablist"
          aria-label="App sections"
          className="mx-auto mt-10 flex w-fit gap-1 rounded-full border border-slate-200 bg-white p-1 shadow-sm"
        >
          {tabs.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={key === activeKey}
              onClick={() => setActiveKey(key)}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition md:px-5 ${
                key === activeKey
                  ? "bg-slate-900 text-white"
                  : "text-slate-500 hover:text-slate-900"
              }`}
            >
              <Icon size={18} />
              {label}
            </button>
          ))}
        </div>

        <div
          role="tabpanel"
          className="mt-14 grid items-center gap-12 md:grid-cols-2 md:gap-16"
        >
          {/* All three screenshots share one fixed-ratio frame and crossfade,
              so switching tabs never changes the layout while an image loads. */}
          <div className="relative mx-auto aspect-[1290/2796] w-full max-w-[340px]">
            <div
              aria-hidden
              className="absolute inset-8 rounded-full bg-gradient-to-br from-blue-400/40 to-violet-400/40 blur-3xl"
            />
            {tabs.map((tab) => (
              <img
                key={tab.key}
                src={tab.image}
                decoding="async"
                alt={tab.key === activeKey ? tab.alt : ""}
                aria-hidden={tab.key !== activeKey}
                className={`absolute inset-0 h-full w-full object-contain drop-shadow-[0_30px_50px_rgba(15,23,42,0.35)] transition-all duration-500 ease-out ${
                  tab.key === activeKey
                    ? "translate-y-0 opacity-100"
                    : "translate-y-3 opacity-0"
                }`}
              />
            ))}
          </div>

          <div key={active.key} className="vo-pop-in">
            <h3 className="text-3xl font-semibold tracking-tight text-slate-900 md:text-4xl">
              {active.title}
            </h3>
            <p className="mt-4 text-lg leading-8 text-slate-600">
              {active.description}
            </p>
            <ul className="mt-8 space-y-4">
              {active.points.map((point) => (
                <li key={point} className="flex items-start gap-3 text-slate-700">
                  <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-600 text-white">
                    <BiCheck size={16} />
                  </span>
                  {point}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductTour;
